import { Component, input, output, forwardRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-slider',
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SliderComponent),
      multi: true
    }
  ],
  template: `
    <div class="flex flex-col gap-2">
      @if (label()) {
        <div class="flex items-center justify-between">
          <label 
            [for]="id() || name()"
            class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
            {{ label() }}
            @if (required()) {
              <span class="text-red-500 ml-1">*</span>
            }
          </label>
          @if (showValue()) {
            <span class="text-sm font-medium text-gray-600 dark:text-gray-400">
              {{ displayValue() }}
            </span>
          }
        </div>
      }
      
      <div class="relative">
        <input
          type="range"
          [id]="id() || name()"
          [name]="name()"
          [min]="min()"
          [max]="max()"
          [step]="step()"
          [disabled]="disabled()"
          [class]="sliderClasses"
          [value]="value()"
          (input)="onInput($event)"
          (blur)="onBlur()"
          [style]="getSliderStyle()" />
        
        @if (showTicks()) {
          <div class="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
            <span>{{ min() }}</span>
            <span>{{ max() }}</span>
          </div>
        }
      </div>
      
      @if (helpText() && !hasError) {
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ helpText() }}
        </span>
      }
      
      @if (hasError) {
        <span class="text-xs text-red-500 dark:text-red-400 font-medium">
          {{ displayError }}
        </span>
      }
    </div>
  `,
  styles: []
})
export class SliderComponent implements ControlValueAccessor {
  label = input<string>('');
  min = input<number>(0);
  max = input<number>(100);
  step = input<number>(1);
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  showValue = input<boolean>(true);
  showTicks = input<boolean>(false);
  errorMessage = input<string>('');
  helpText = input<string>('');
  id = input<string>('');
  name = input<string>('');
  class = input<string>('');
  size = input<ComponentSize>('md');

  valueChange = output<number>();

  value = signal<number>(this.min());
  touched = signal(false);

  private onChange = (value: number) => {};
  private onTouched = () => {};

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = parseFloat(target.value);
    this.value.set(newValue);
    this.onChange(newValue);
    this.valueChange.emit(newValue);
    this.markAsTouched();
  }

  onBlur(): void {
    this.markAsTouched();
    this.onTouched();
  }

  displayValue(): string {
    if (this.showValue()) {
      return this.value().toString();
    }
    return '';
  }

  getSliderStyle(): { [key: string]: string } {
    const percentage = ((this.value() - this.min()) / (this.max() - this.min())) * 100;
    return {
      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`
    };
  }

  get sliderClasses(): string {
    const sizeMap = {
      xs: 'h-1',
      sm: 'h-1.5',
      md: 'h-2',
      lg: 'h-2.5',
      xl: 'h-3'
    };

    const classes = [
      'w-full',
      'appearance-none',
      'cursor-pointer',
      'rounded-lg',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      sizeMap[this.size()]
    ];

    if (this.disabled()) {
      classes.push('opacity-50', 'cursor-not-allowed');
    }

    const customClass = this.class();
    if (customClass) {
      classes.push(customClass);
    }

    return classes.join(' ');
  }

  get hasError(): boolean {
    return this.touched() && !!this.errorMessage();
  }

  get displayError(): string {
    return this.errorMessage() || '';
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: number): void {
    this.value.set(value ?? this.min());
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }
}

